import React, { useMemo } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { GroupCollapseIcon } from '../../../../global/group-collapse-icon/group-collapse-icon';
import UploadButton, { FILE_TYPE } from '../../shared/upload-button';
import DefaultButton from '../../../../global/default-button/default-button';
import { Category, IAssetsListItemDTO, ITitleAssetUrlDTO } from '../../../../../api/assets.dao';
import classnames from 'classnames';
import { AssetsFileFormWrapper } from '../assets-file-form/assets-file-form-wrapper';
import { MaterialSpinner } from '../../../../global/spinner/material-spinner';

import '../assets.scss';
import { Tooltip } from '../../../../global/tooltip/tooltip';
import AssetLinkWrapper from '../asset-link/asset-link-wrapper';
import { getReportDateTimeString } from '../../../../../infrastructure/utils/date.util';
import { ASSETS_DROP_ZONE_TEXT, ASSETS_UPLOADING_TEXT } from '../assets.text';

interface IAssetsFileRowProps {
    file: ITitleAssetUrlDTO;
    accountId: number;
    eventId: number;
    onDeleteAsset: (objectKey: string) => void;
    handleDragStart: (e: React.DragEvent<HTMLDivElement>, file: ITitleAssetUrlDTO) => void;
    draggingFile: ITitleAssetUrlDTO;
    handleDragEnd: () => void;
    updateAttendeePermission: boolean;
    protectedCategory: boolean;
    isLinkCreating: boolean;
    createLink: (assetMetadataId: number, afterCreateLinkCb: () => void) => Promise<void>;
    onDeleteAssetDownloadLink: (assetMetadataId: number) => void;
    deleteAssetDownloadLinkPermission: boolean;
}

const AssetsFileRow = ({ file, accountId, eventId, onDeleteAsset, handleDragStart, draggingFile,
    handleDragEnd, updateAttendeePermission, protectedCategory, isLinkCreating,
    createLink, onDeleteAssetDownloadLink, deleteAssetDownloadLinkPermission }: IAssetsFileRowProps) => {
    const handleDragStartForFile = (e: React.DragEvent<HTMLDivElement>) => handleDragStart(e, file);
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

    return (
    <tr className='assets-list-item'>
        <td className='hoverable assets-list-item__date'>
            {file.uploadDateTime ? getReportDateTimeString(new Date(file.uploadDateTime)) : ''}
        </td>
        <td className='hoverable'>
            <Tooltip content={file.title} className='oe-tooltip--assets-offset'>
                <div
                    draggable={updateAttendeePermission}
                    onDragStart={handleDragStartForFile}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                >
                    <AssetsFileFormWrapper
                        accountId={accountId}
                        eventId={eventId}
                        file={file}
                        disabled={draggingFile && file && draggingFile.objectKey === file.objectKey}
                        onDeleteAsset={onDeleteAsset}
                        fileProtected={protectedCategory}
                    />
                </div>
            </Tooltip>
        </td>
        <td className='hoverable'>
            <AssetLinkWrapper
                isLinkCreating={isLinkCreating}
                file={file}
                createLink={createLink}
                deleteAssetDownloadLinkPermission={deleteAssetDownloadLinkPermission}
                onDeleteAssetDownloadLink={onDeleteAssetDownloadLink}
            />
        </td>
    </tr>
    );
};

export { AssetsFileRow };

interface IProps {
    onExpandGroup: (group: string) => void;
    onCollapseGroup: (group: string) => void;
    collapsedGroups: string[];
    categoryGroup: { type: string; label: string };
    colSpan: number;
    selectedFiles: File[];
    handleUpdateAssets?: (files: File[]) => void;
    assetsList: IAssetsListItemDTO;
    isUploadLoading: boolean;
    accountId: number;
    eventId: number;
    onDeleteAsset: (objectKey: string) => void;
    handleDragStart: (e: React.DragEvent<HTMLDivElement>, file: ITitleAssetUrlDTO) => void;
    draggingFile: ITitleAssetUrlDTO;
    handleDragEnd: () => void;
    uploadEventAssetsPermission: boolean;
    updateAttendeePermission: boolean;
    deleteAssetDownloadLinkPermission: boolean;
    protectedCategory: boolean;
    isLinkCreating: Set<number>;
    createLink: (assetMetadataId: number, afterCreateLinkCb: () => void) => Promise<void>;
    onDeleteAssetDownloadLink: (assetMetadataId: number) => void;
    isDraggingOverPage: boolean;
    hasFiles: boolean;
    fileError: string | JSX.Element;
}

const AssetsListItem = mobxReact.observer((props: IProps) => {
    const {
        collapsedGroups,
        onExpandGroup,
        onCollapseGroup,
        categoryGroup,
        colSpan,
        selectedFiles,
        handleUpdateAssets,
        assetsList,
        isUploadLoading,
        accountId,
        eventId,
        onDeleteAsset,
        handleDragStart,
        draggingFile,
        handleDragEnd,
        uploadEventAssetsPermission,
        updateAttendeePermission,
        protectedCategory,
        isLinkCreating,
        createLink,
        onDeleteAssetDownloadLink,
        deleteAssetDownloadLinkPermission,
        isDraggingOverPage,
        hasFiles,
        fileError,
    } = props;

    const isGroupCollapsed = useMemo(() => collapsedGroups.some(el =>
        el === categoryGroup.type
    ), [collapsedGroups, categoryGroup.type]);

    const handleCollapseGroupToggle = () => {
        if (isGroupCollapsed) {
            onExpandGroup(categoryGroup.type);
        } else {
            onCollapseGroup(categoryGroup.type);
        }
    };

    const handleFilesUpload = (files: File[]) => {
        try {
            handleUpdateAssets(files);
        // eslint-disable-next-line no-empty
        } catch (e) {}
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableCellElement>) => e.preventDefault();

    const handleDrop = (e: React.DragEvent<HTMLTableCellElement>) => {
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        handleFilesUpload(Array.from(e.dataTransfer.files));
    };

    const isUncategorizedFiles = categoryGroup.type === Category.UNCATEGORIZED;
    const lengthUncategorizedFiles = assetsList?.files?.length || 0;

    const shouldShowEmptyDndZone = !isGroupCollapsed && isUncategorizedFiles && !hasFiles && uploadEventAssetsPermission;
    const shouldShowDropZone = !isGroupCollapsed && isUncategorizedFiles && isDraggingOverPage && hasFiles;
    const shouldShowPendingFiles = !isGroupCollapsed && isUncategorizedFiles;
    const shouldShowUploadedFiles = !isGroupCollapsed;

    return (
        <>
            <tr className='assets-list-group-header' >
                <th colSpan={colSpan} className={classnames({ 'assets-list-group-header__collapsed': isGroupCollapsed })}>
                    <div className='assets-list-group-header__title-block' onClick={handleCollapseGroupToggle}>
                        <GroupCollapseIcon isCollapsed={isGroupCollapsed} isAllItemsCollapsed={false} />
                        <div className='assets-list-group-header__title'>
                            {categoryGroup.label} {lengthUncategorizedFiles ? `(${lengthUncategorizedFiles})` : ''}
                        </div>
                    </div>
                </th>
            </tr>

            {shouldShowEmptyDndZone && (
                <tr>
                    <td
                        colSpan={colSpan}
                        className='assets-empty-dnd-zone'
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <UploadButton
                            multiple
                            uploadedFileType={FILE_TYPE.ASSETS}
                            onChange={handleFilesUpload}
                        />
                    </td>
                </tr>
            )}

            {shouldShowDropZone && (
                <>
                    <tr className='assets-drop-zone-row assets-drop-zone-row--active'>
                        <td colSpan={colSpan} onDragOver={handleDragOver} onDrop={handleDrop}>
                            <FormattedMessage {...ASSETS_DROP_ZONE_TEXT} />
                        </td>
                    </tr>
                    <tr className='assets-drop-zone-row__spacer'>
                        <td colSpan={colSpan} />
                    </tr>
                </>
            )}

            {fileError && (
                <tr>
                    <td colSpan={colSpan} className='assets-layout__file-error merge-with-previous-row'>
                        <span className='oe-form-field__error'>{fileError}</span>
                    </td>
                </tr>
            )}

            {shouldShowPendingFiles && selectedFiles.map((file, index) => {
                return (
                    <tr key={index} className={classnames('assets-list-item', { 'assets-list-item__disabled': isUploadLoading })}>
                        <td className='hoverable'>
                            {isUploadLoading && (
                                <div className='assets-list-item__uploading'>
                                    <MaterialSpinner radius={14} stroke={2} />
                                    <FormattedMessage {...ASSETS_UPLOADING_TEXT} />
                                </div>
                            )}
                        </td>
                        <td className='hoverable'>
                            <DefaultButton
                                modifier={['slim', 'link']}
                                text={file.name}
                                className='download-item'
                                withEllipsis
                            />
                        </td>
                        <td className='hoverable' />
                    </tr>
                );
            })}

            {shouldShowUploadedFiles
                && assetsList?.files?.map((file, index) => (
                    <AssetsFileRow
                        key={index}
                        file={file}
                        accountId={accountId}
                        eventId={eventId}
                        onDeleteAsset={onDeleteAsset}
                        handleDragStart={handleDragStart}
                        draggingFile={draggingFile}
                        handleDragEnd={handleDragEnd}
                        updateAttendeePermission={updateAttendeePermission}
                        protectedCategory={protectedCategory}
                        isLinkCreating={isLinkCreating.has(file.assetMetadataId)}
                        createLink={createLink}
                        onDeleteAssetDownloadLink={onDeleteAssetDownloadLink}
                        deleteAssetDownloadLinkPermission={deleteAssetDownloadLinkPermission}
                    />
                ))
            }
        </>
    );
});

export { AssetsListItem };
